import React, { useState } from "react";
import * as XLSX from "xlsx";
import styled from "styled-components";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import BASE_API_URL from "../apis/index";

import { PRODUCT_OPTIONS } from "../constants/productsOptions";
const currencyValues = PRODUCT_OPTIONS.currencies.map((c) => c.value);

/* ---------------- OPTIONS ---------------- */
const CURRENCY_OPTIONS = PRODUCT_OPTIONS.currencies.map((c) => c.value);
const UNIT_OPTIONS = PRODUCT_OPTIONS.units;

/* ---------------- HELPERS ---------------- */
const toArray = (val) => {
  if (!val) return [];
  return val
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const validateRow = (row) => {
  const errors = [];

  if (!row.productName?.trim()) errors.push("Product Name missing");
  if (
    row.price === "" ||
    row.price === null ||
    row.price === undefined ||
    isNaN(Number(row.price))
  ) {
    errors.push("Price is required and must be a number");
  }
  if (!row.moq || isNaN(Number(row.moq))) {
    errors.push("MOQ is required and must be a number");
  }

  if (!PRODUCT_OPTIONS.categories.includes(row.category))
    errors.push(`Invalid Category (${row.category})`);

  if (!CURRENCY_OPTIONS.includes(row.currency))
    errors.push(`Invalid Currency (${row.currency})`);

  if (!UNIT_OPTIONS.includes(row.unit))
    errors.push(`Invalid Unit (${row.unit})`);

  if (
    row.productType &&
    !PRODUCT_OPTIONS.productTypes.includes(row.productType)
  )
    errors.push(`Invalid Product Type`);

  if (
    row.formatsAvailable &&
    !PRODUCT_OPTIONS.formats.includes(row.formatsAvailable)
  )
    errors.push(`Invalid Format`);

  // ✅ Features validation
  const normalize = (val) => val.trim().toLowerCase();

  const invalidFeatures = row.features.filter(
    (f) => !PRODUCT_OPTIONS.features.map(normalize).includes(normalize(f)),
  );
  if (invalidFeatures.length) {
    errors.push(`Invalid Features: ${invalidFeatures.join(", ")}`);
  }

  // ✅ Grades validation
  const invalidGrades = row.grades.filter(
    (g) => !PRODUCT_OPTIONS.grades.map(normalize).includes(normalize(g)),
  );
  if (invalidGrades.length) {
    errors.push(`Invalid Grades: ${invalidGrades.join(", ")}`);
  }

  // Atleast one greade required
  if (!Array.isArray(row.grades) || row.grades.length === 0) {
    errors.push("At least one Grade is required");
  }

  if (isNaN(row.price)) errors.push("Invalid Price");

  return errors;
};

const transformRow = (raw, idx) => ({
  _id: idx,
  productName: raw["Product Name"] || "",
  description: raw["Description"] || "",
  category: raw["Category"] || "",
  price: Number(raw["Price"]) || "",
  currency: raw["Currency"] || "INR",
  productType: raw["Product Type"] || "",
  concentration: raw["Concentration %"] || "",
  source: raw["Source"] || "",
  formatsAvailable: raw["Formats available"] || "",
  moq: raw["MOQ"] || "",
  unit: raw["Unit"] || "",
  features: toArray(raw["Features"]),
  grades: toArray(raw["Grades"]),
  technicalDetails: raw["Technical Details"] || "",
});

/* ---------------- COMPONENT ---------------- */
const UserProductsRegistrationBulkUpload = ({ compId, companyType }) => {
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [duplicateError, setDuplicateError] = useState(null);

  const revalidate = (rows) => {
    const newErrors = {};
    rows.forEach((row) => {
      const err = validateRow(row);
      if (err.length) newErrors[row._id] = err;
    });
    setErrors(newErrors);
    return newErrors;
  };

  // Check for Duplicates in DB
  const checkDuplicates = async (rows) => {
    try {
      const res = await axios.post(
        `${BASE_API_URL}/api/seller/product/check-bulk/${compId}`,
        {
          products: rows,
          company_type: companyType,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      return res.data;
    } catch (err) {
      if (err.response?.status === 409) {
        return err.response.data;
      }
      throw err;
    }
  };

  const checkExcelDuplicates = (rows) => {
    const seen = new Map();
    const duplicates = [];

    const normalize = (val) => (val || "").toString().trim().toLowerCase();

    rows.forEach((p, index) => {
      const key = `${normalize(p.productName)}-${normalize(p.category)}-${normalize(p.concentration)}`;

      if (seen.has(key)) {
        duplicates.push(index + 1);
        duplicates.push(seen.get(key) + 1);
      } else {
        seen.set(key, index);
      }
    });

    return [...new Set(duplicates)].sort((a, b) => a - b); // ✅ sorted
  };

  // Handles file when uploaded and check for duplicate rows
  const handleFile = (file) => {
    if (!file) return;

    setProducts([]);
    setErrors({});
    setDuplicateError(null);

    const reader = new FileReader();

    // reader.onload = async (e) => {
    //     const workbook = XLSX.read(e.target.result, { type: "array" });
    //     const sheet = workbook.Sheets[workbook.SheetNames[0]];
    //     const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    //     const rows = json.map((r, i) => transformRow(r, i));
    //     setProducts(rows);
    //     const errs = revalidate(rows);
    //     if (Object.keys(errs).length > 0) {
    //         return; // stop here if validation errors exist
    //     }
    //     const excelDup = checkExcelDuplicates(rows);
    //     if (excelDup.length > 0) {
    //         setDuplicateError({
    //             excelDuplicates: excelDup,
    //             dbDuplicates: []
    //         });
    //         setSnackbar({
    //             open: true,
    //             severity: "error",
    //             message: `Duplicate rows in Excel: ${excelDup.join(", ")}`
    //         });
    //         return; // ❌ stop here
    //     }
    //     // ✅ CALL BACKEND CHECK
    //     const result = await checkDuplicates(rows);
    //     if (result.status === "error") {
    //         setDuplicateError(result);
    //         setSnackbar({
    //             open: true,
    //             severity: "error",
    //             message: `Excel duplicates: ${result.excelDuplicates.join(", ")} | DB duplicates: ${result.dbDuplicates.join(", ")}`
    //         });
    //         return;
    //     }
    //     setDuplicateError(null);
    // };
    reader.onload = async (e) => {
      const workbook = XLSX.read(e.target.result, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const rows = json.map((r, i) => transformRow(r, i));
      setProducts(rows);

      // ✅ 1. Row validation
      const rowErrors = revalidate(rows);

      // ✅ 2. Excel duplicates
      const excelDup = checkExcelDuplicates(rows);

      // ✅ 3. DB duplicates (call ALWAYS)
      let dbDup = [];
      try {
        const result = await checkDuplicates(rows);

        if (result?.status === "error") {
          dbDup = result.dbDuplicates || [];
        }
      } catch (e) {
        console.error("DB check failed", e);
      }

      // ✅ 4. Set duplicate state (combined)
      if (excelDup.length || dbDup.length) {
        setDuplicateError({
          excelDuplicates: excelDup,
          dbDuplicates: dbDup,
        });
      } else {
        setDuplicateError(null);
      }

      // ✅ 5. Snackbar message (ALL errors together)
      let msg = "";

      if (Object.keys(rowErrors).length) {
        msg += `Invalid rows: ${Object.keys(rowErrors).map(i => Number(i)+1).join(", ")}. `;
      }

      if (excelDup.length) {
        msg += `Excel duplicates: ${excelDup.join(", ")}. `;
      }

      if (dbDup.length) {
        msg += `DB duplicates: ${dbDup.join(", ")}.`;
      }

      if (msg) {
        setSnackbar({
          open: true,
          severity: "error",
          message: msg,
        });
      }
    };

    reader.readAsArrayBuffer(file); // ✅ CORRECT PLACE
  };

  // Handles Submit of sheet
  const handleSubmit = async () => {
    const errs = revalidate(products);

    if (Object.keys(errs).length) {
      setSnackbar({
        open: true,
        message: "Fix errors before submitting",
        severity: "error",
      });
      return;
    }

    // ✅ use stored file
    if (!uploadedFile) {
      alert("Please upload file again");
      return;
    }

    try {
      const formData = new FormData();

      formData.append("file", uploadedFile);
      formData.append("products", JSON.stringify(products));
      formData.append("company_type", companyType);

      await axios.post(
        `${BASE_API_URL}/api/seller/product/bulk-register/${compId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // ✅ reset everything after success
      setProducts([]);
      setErrors({});
      setUploadedFile(null);

      setSnackbar({
        open: true,
        message: "Products uploaded successfully",
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Upload failed",
        severity: "error",
      });
    }
  };

  return (
    <Wrapper>
      {/* Upload */}
      <div
        className="upload-area"
        onClick={() => document.getElementById("excel-input").click()}
      >
        <CloudUploadIcon />
        <Typography>Upload Excel</Typography>
        <input
          id="excel-input"
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            const file = e.target.files[0];

            if (!file) return;

            setUploadedFile(file); // ✅ store file
            handleFile(file); // parse data

            e.target.value = ""; // ✅ still allow re-upload
          }}
        />
      </div>

      <div className="my-4">
        {/* Errors */}
        {Object.entries(errors).map(([row, errs]) => (
          <Alert severity="error" key={row}>
            Row {Number(row) + 1}: {errs.join(", ")}
          </Alert>
        ))}

        {duplicateError && (
          <Alert severity="error">
            {duplicateError.excelDuplicates.length > 0 && (
              <div>
                Excel duplicate rows:{" "}
                {duplicateError.excelDuplicates.join(", ")}
              </div>
            )}
            {duplicateError.dbDuplicates.length > 0 && (
              <div>
                Product already exist in the system with same Name, Category &
                Concentration: {duplicateError.dbDuplicates.join(", ")}
              </div>
            )}
          </Alert>
        )}

        {/* Table */}
        {products.length > 0 && (
          <>
            <div style={{ overflowX: "auto" }}>
              <TableContainer>
                <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Product Type</TableCell>
                      <TableCell>Concentration %</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Formats</TableCell>
                      <TableCell>MOQ</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Features</TableCell>
                      <TableCell>Grades</TableCell>
                      <TableCell>Technical Details</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {products.map((row, idx) => (
                      <TableRow key={row._id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{row.productName}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell>{row.price}</TableCell>
                        <TableCell>{row.currency}</TableCell>
                        <TableCell>{row.productType}</TableCell>
                        <TableCell>{row.concentration}</TableCell>
                        <TableCell>{row.source}</TableCell>
                        <TableCell>{row.formatsAvailable}</TableCell>
                        <TableCell>{row.moq}</TableCell>
                        <TableCell>{row.unit}</TableCell>

                        <TableCell>{row.features.join(", ")}</TableCell>

                        <TableCell>{row.grades.join(", ")}</TableCell>

                        <TableCell>{row.technicalDetails}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <Button
              variant="contained"
              onClick={handleSubmit}
              endIcon={<SendIcon />}
              disabled={Object.keys(errors).length > 0 || duplicateError}
            >
              Submit
            </Button>
          </>
        )}
      </div>

      {/* Instructions secion */}
      <>
        <div className="upload-instructions">
          <Typography className="instructions-title">
            Excel Upload Instructions
          </Typography>

          <ul>
            <li>
              <b>Required fields:</b> Product Name, Category, Price, Currency,
              MOQ, Unit, Grades
            </li>

            <li>
              <b>Dropdown Fields (must match exactly):</b>
              <ul>
                <li>
                  <b>Category:</b> {PRODUCT_OPTIONS.categories.join(", ")}
                </li>
                <li>
                  <b>Currency:</b> {currencyValues.join(", ")}
                </li>
                <li>
                  <b>Unit:</b> {PRODUCT_OPTIONS.units.join(", ")}
                </li>
                <li>
                  <b>Product Type:</b> {PRODUCT_OPTIONS.productTypes.join(", ")}
                </li>
                <li>
                  <b>Formats Available:</b> {PRODUCT_OPTIONS.formats.join(", ")}
                </li>
              </ul>
            </li>

            <li>
              <b>Features & Grades:</b>
              <ul>
                <li>You can enter options for Features as given below:</li>
                <li>
                  <b>Features:</b> {PRODUCT_OPTIONS.features.join(", ")}
                </li>
                <li>
                  <b>Grades:</b> {PRODUCT_OPTIONS.grades.join(", ")}
                </li>
                <li>Separate multiple options using comma</li>
                <li>
                  <strong>
                    {" "}
                    Any other options for features and Grades that are not
                    included in above given options are not acceptable.{" "}
                  </strong>
                </li>
              </ul>
            </li>

            <li>
              <b>Price</b> and <b>MOQ</b> must be numeric values only.
            </li>
            <li>
              <b>Maximum limit:</b> 500 products per upload.
            </li>
          </ul>
        </div>
      </>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} // ✅ THIS LINE
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
};

export default UserProductsRegistrationBulkUpload;

const Wrapper = styled.div`
  .upload-area {
    border: 2px dashed #d0c9c5;
    border-radius: 14px;
    padding: 48px 24px;
    text-align: center;
    background: #fff9f6;
    transition:
      border-color 0.2s,
      background 0.2s;
    margin-bottom: 32px;
    cursor: pointer;
  }
  .upload-area:hover,
  .upload-area.drag-over {
    border-color: #edc0a4;
    background: #fff8ee;
  }
  .upload-area input[type="file"] {
    display: none;
  }
  .upload-hint {
    margin-top: 12px;
    font-size: 0.82rem;
    color: #999;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .MuiSnackbar-anchorOriginBottomRight {
    .MuiAlert-message {
      color: white;
    }
    svg path {
      fill: white;
    }
  }

  /* ---- Upload Instructions ---- */
  .upload-instructions {
    margin-top: 28px;
    padding: 18px 22px;
    border-radius: 12px;
    background: #fafafa;
    border: 1px solid #eee;
  }
  .instructions-title {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 10px;
    color: #444;
  }
  .upload-instructions ul {
    padding-left: 18px;
    margin: 0;
  }
  .upload-instructions li {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 6px;
    line-height: 1.45;
  }
`;
